import {
  Alert,
  Modal,
  Select,
  SelectOptionElement,
} from '@odin-labs/components';
import { useGetSiteGatewaysQuery } from 'apollo/generated/client-operations';
import React from 'react';
import type { Zone } from 'siteLayout';

const classes = {
  body: 'odin-flex odin-flex-col odin-gap-4',
  lead: 'odin-text-sm odin-text-gray-900',
};

export type GatewayDestinationsModalProps = {
  siteId: string;
  /** The gateways the seed needs a zone for; null when closed. */
  gatewayIds: string[] | null;
  zones: Zone[];
  /** The zone chosen so far for each gateway, by gateway id. */
  destinations: Record<string, string>;
  onChange: (gatewayId: string, zoneId: string) => void;
  /** What stopped the last attempt, shown until the next one. */
  error: string | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

/**
 * Asks where each of the site's gateways leads when the draft has several
 * zones: each becomes an entry from the outside into the zone chosen.
 */
export function GatewayDestinationsModal({
  siteId,
  gatewayIds,
  zones,
  destinations,
  onChange,
  error,
  busy,
  onCancel,
  onSubmit,
}: GatewayDestinationsModalProps): React.ReactElement {
  const { data } = useGetSiteGatewaysQuery({
    variables: { siteId },
    skip: !gatewayIds,
    fetchPolicy: 'no-cache',
  });
  const names = new Map(
    (data?.getJobsite?.jobsiteGateways.edges ?? []).map(({ node }) => [
      node.id,
      node.name,
    ]),
  );
  const options: SelectOptionElement[] = zones.map((zone) => ({
    value: zone.id,
    label: zone.name,
  }));
  const ids = gatewayIds ?? [];
  const complete = ids.every((id) => destinations[id]);
  return (
    <Modal
      open={gatewayIds !== null}
      setOpen={(open) => !open && onCancel()}
      title="Choose a zone for each gateway"
      size="md"
      actionText="Add entries"
      actionButtonEnabled={complete && !busy}
      actionButtonWithSpinner={busy}
      onAction={onSubmit}
      cancelText="Cancel"
      onCancel={onCancel}
    >
      <div className={classes.body}>
        <p className={classes.lead}>
          Each gateway becomes an entry from Outside into the zone you choose,
          with both directions set to None.
        </p>
        {error && <Alert type="danger" text={error} />}
        {ids.map((id) => (
          <Select
            key={id}
            name={`destination-${id}`}
            label={names.get(id) ?? 'Gateway'}
            value={
              options.find((option) => option.value === destinations[id]) ??
              null
            }
            options={options}
            isDisabled={busy}
            isSearchable={false}
            isClearable={false}
            onChange={(option: SelectOptionElement | null) => {
              if (option) onChange(id, option.value);
            }}
          />
        ))}
      </div>
    </Modal>
  );
}
